/**
 * Viluu landing GA4 helpers (Consent Mode v2 + registration funnel events).
 *
 * Key events (mark in GA4 Admin): sign_up_start, sign_up, doi_confirmed, app_store_click
 * Event-scoped custom dimensions: cta_location, step_number, step_name, method, …
 */

export const GA_MEASUREMENT_ID = 'G-1ZRVB4LQ90';
export const PLAY_STORE_BASE =
  'https://play.google.com/store/apps/details?id=de.viluu.mobile';

export const STEP_NAMES = {
  1: 'birthday',
  2: 'location',
  3: 'matching',
  4: 'intent',
  5: 'account',
};

export function trackEvent(eventName, params = {}) {
  if (typeof window === 'undefined') {
    return;
  }
  const payload = { ...params, send_to: GA_MEASUREMENT_ID };
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, payload);
    return;
  }
  // Mirror gtag() Arguments push so queued hits work once the tag loads.
  (function () {
    window.dataLayer.push(arguments);
  })('event', eventName, payload);
}

export function resolveCtaMeta(el, defaults = {}) {
  const fromEl = {
    cta_location:
      (el && el.getAttribute && el.getAttribute('data-cta-location')) || null,
  };
  return {
    cta_location: fromEl.cta_location || defaults.cta_location || 'unknown',
  };
}

function getCookie(name) {
  if (typeof document === 'undefined') {
    return null;
  }
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop().split(';').shift();
  }
  return null;
}

/**
 * GA4 client ID from the `_ga` cookie: GA1.x.XXXXXXXXXX.YYYYYYYYYY → XXXXXXXXXX.YYYYYYYYYY
 */
export function readGaClientId() {
  const raw = getCookie('_ga');
  if (!raw) {
    return null;
  }
  const parts = String(raw).split('.');
  if (parts.length < 4) {
    return null;
  }
  return `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
}

/**
 * GA4 session ID from `_ga_<MEASUREMENT_SUFFIX>` cookie when present.
 */
export function readGaSessionId() {
  const suffix = GA_MEASUREMENT_ID.replace(/^G-/, '');
  const raw = getCookie(`_ga_${suffix}`);
  if (!raw) {
    return null;
  }
  // Format: GS2.1.<session_id>.… (or GS1.1.… on older tags)
  const parts = String(raw).split('.');
  if (parts.length >= 3 && /^\d+$/.test(parts[2])) {
    return parts[2];
  }
  return null;
}

export function hasStatisticsConsent() {
  try {
    const raw = getCookie('viluu_consent_v1');
    if (!raw) {
      return false;
    }
    const record = JSON.parse(decodeURIComponent(raw));
    return !!(record && record.statistics);
  } catch (e) {
    return false;
  }
}

function pageUtmParams() {
  if (typeof window === 'undefined' || !window.location) {
    return {};
  }
  const params = new URLSearchParams(window.location.search || '');
  const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  const out = {};
  keys.forEach((key) => {
    const value = params.get(key);
    if (value) {
      out[key] = value;
    }
  });
  return out;
}

/**
 * Play Store URL with referrer UTMs for install attribution.
 */
export function buildPlayStoreUrl(ctaLocation) {
  const pageUtms = pageUtmParams();
  const referrerParts = [
    `utm_source=${encodeURIComponent(pageUtms.utm_source || 'viluu.de')}`,
    `utm_medium=${encodeURIComponent(pageUtms.utm_medium || 'website')}`,
    `utm_campaign=${encodeURIComponent(
      pageUtms.utm_campaign || String(ctaLocation || 'unknown')
    )}`,
  ];
  if (pageUtms.utm_content) {
    referrerParts.push(`utm_content=${encodeURIComponent(pageUtms.utm_content)}`);
  }
  if (pageUtms.utm_term) {
    referrerParts.push(`utm_term=${encodeURIComponent(pageUtms.utm_term)}`);
  }
  const url = new URL(PLAY_STORE_BASE);
  url.searchParams.set('referrer', referrerParts.join('&'));
  return url.toString();
}

/**
 * Snapshot for server-side SOI attribution (Measurement Protocol DOI).
 */
export function buildAnalyticsContext(extra = {}) {
  const consentGranted = hasStatisticsConsent();
  return {
    client_id: consentGranted ? readGaClientId() : null,
    session_id: consentGranted ? readGaSessionId() : null,
    consent: consentGranted ? 'granted' : 'denied',
    cta_location: extra.cta_location || null,
    method: extra.method || null,
  };
}

export async function stashAnalyticsContext(extra = {}) {
  const payload = buildAnalyticsContext(extra);
  try {
    await axios.post(zRoute('ajax.analytics.context'), payload);
  } catch (e) {
    // Non-blocking: registration should still succeed without analytics stash.
  }
  return payload;
}
